import { useEffect, useRef, useState } from 'react'
import type { MenuCategory } from '../../api/types'

interface Props {
  categories: MenuCategory[]
}

export function CategoryNav({ categories }: Props) {
  const [activeId, setActiveId] = useState<number | null>(
    categories[0]?.id ?? null,
  )
  const chipRefs = useRef<Map<number, HTMLButtonElement>>(new Map())

  // Scroll listener: activate the last section whose heading is above the detection line.
  // This is simpler and more reliable than IntersectionObserver for sticky nav sync.
  useEffect(() => {
    if (categories.length === 0) return

    const DETECTION_OFFSET = 120 // px below viewport top (clears header + nav)

    function syncActive() {
      // Bottom-of-page: always activate the last category
      const atBottom =
        document.documentElement.scrollHeight - window.scrollY - window.innerHeight < 8
      if (atBottom) {
        setActiveId(categories[categories.length - 1].id)
        return
      }

      // Otherwise: last section whose top edge has crossed the detection line
      let active = categories[0].id
      for (const cat of categories) {
        const el = document.getElementById(`category-${cat.id}`)
        if (!el) continue
        if (el.getBoundingClientRect().top <= DETECTION_OFFSET) {
          active = cat.id
        }
      }
      setActiveId(active)
    }

    window.addEventListener('scroll', syncActive, { passive: true })
    syncActive() // run once on mount so first render is correct
    return () => window.removeEventListener('scroll', syncActive)
  }, [categories])

  // Scroll active chip into view when activeId changes
  useEffect(() => {
    if (activeId == null) return
    const btn = chipRefs.current.get(activeId)
    btn?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }, [activeId])

  function scrollToCategory(id: number) {
    const el = document.getElementById(`category-${id}`)
    if (!el) return
    // Offset for the sticky nav height (~48px) + header (~60px)
    const top = el.getBoundingClientRect().top + window.scrollY - 112
    window.scrollTo({ top, behavior: 'smooth' })
  }

  if (categories.length === 0) return null

  return (
    <nav
      aria-label="Menu categories"
      className="sticky top-0 z-10 bg-white border-b border-neutral-100 shadow-sm"
    >
      <ol
        className="flex gap-3 overflow-x-auto px-4 py-2.5"
        style={{ scrollbarWidth: 'none' }}
      >
        {categories.map((cat) => {
          const isActive = cat.id === activeId
          return (
            <li key={cat.id} className="shrink-0">
              <button
                ref={(el) => {
                  if (el) chipRefs.current.set(cat.id, el)
                  else chipRefs.current.delete(cat.id)
                }}
                onClick={() => scrollToCategory(cat.id)}
                className={
                  isActive
                    ? 'rounded-[var(--chip-radius)] px-4 py-3 text-sm font-semibold font-body bg-brand-accent text-[var(--brand-accent-text)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2'
                    : 'rounded-[var(--chip-radius)] px-4 py-3 text-sm font-medium font-body bg-neutral-100 text-neutral-600 hover:bg-neutral-200 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2'
                }
                aria-current={isActive ? 'true' : undefined}
              >
                {cat.name}
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
